import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import Button from '../../components/shared/Button';
import Modal from '../../components/shared/Modal';
import StatusBadge from '../../components/shared/StatusBadge';
import { getDailyReservations, updateReservation } from '../../services/business/reservationService';
import { logLoadingStart, logLoadingEnd, logDockArrival, getVehicleMovementLogByReservation } from '../../services/logging/vehicleMovementLogger';

const LoadingList = ({ userData }) => {
    const { user } = usePermissions(userData);
    const { success, error: toastError } = useToast();

    const [loading, setLoading] = useState(false);
    const [reservations, setReservations] = useState([]);
    const [dockFilter, setDockFilter] = useState('ALL');

    // Modal
    const [selectedJob, setSelectedJob] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [actionType, setActionType] = useState(null); // 'START' | 'FINISH'

    useEffect(() => {
        if (user?.tenantId) {
            loadJobs();
            const interval = setInterval(loadJobs, 60000); // 1 min refresh
            return () => clearInterval(interval);
        }
    }, [user?.tenantId]);

    const loadJobs = async () => {
        setLoading(true);
        try {
            const todayStr = new Date().toISOString();
            const data = await getDailyReservations(user.tenantId, todayStr);

            // Filter only jobs relevant to docks:
            // - checked_in (waiting for dock)
            // - dock_assigned (waiting at dock? We usually skip this state and go checked_in -> loading_started if gate doesn't assign specific dock, 
            //   but if coord assigned dock, they are checked_in)
            // - loading_started
            // - loading_completed (history for today)
            // - pending (scheduled but not arrived)

            const relevantStatuses = ['pending', 'checked_in', 'loading_started', 'loading_completed'];
            const dockJobs = data.filter(r => relevantStatuses.includes(r.status));
            setReservations(dockJobs);
        } catch (err) {
            console.error(err);
            toastError("Yükleme listesi alınamadı.");
        } finally {
            setLoading(false);
        }
    };

    const handleActionClick = (job, type) => {
        setSelectedJob(job);
        setActionType(type);
        setIsModalOpen(true);
    };

    const performAction = async () => {
        if (!selectedJob || !actionType) return;

        try {
            // Get active log
            const activeLog = await getVehicleMovementLogByReservation(selectedJob.id);
            if (!activeLog) {
                toastError("Sistem hatası: Hareket kaydı bulunamadı (Araç giriş yapmamış olabilir).");
                return;
            }

            if (actionType === 'START') {
                // If status was checked_in, first log LogDockArrival implicit? Or just Start.
                // Let's log Arrival + Start for simplicity if they haven't been logged.

                await logDockArrival(activeLog.id, selectedJob.dockName || 'Belirsiz Rampa', user.uid);
                await logLoadingStart(activeLog.id, selectedJob.dockName || 'Belirsiz Rampa', user.uid);

                await updateReservation(selectedJob.id, {
                    status: 'loading_started',
                    loadingStartTime: new Date()
                }, userData, userData.tenantId);

                success("Yükleme başlatıldı.");
            } else if (actionType === 'FINISH') {
                await logLoadingEnd(activeLog.id, selectedJob.dockName || 'Belirsiz Rampa', user.uid);

                await updateReservation(selectedJob.id, {
                    status: 'loading_completed',
                    loadingEndTime: new Date()
                }, userData, userData.tenantId);

                success("Yükleme tamamlandı.");
            }

            setIsModalOpen(false);
            loadJobs();
        } catch (err) {
            console.error(err);
            toastError("İşlem başarısız: " + err.message);
        }
    };

    const getActionForStatus = (status) => {
        if (status === 'checked_in') return { label: 'Yüklemeyi Başlat', type: 'START', variant: 'primary' };
        if (status === 'loading_started') return { label: 'Yüklemeyi Bitir', type: 'FINISH', variant: 'success' };
        return null;
    };

    // Filter Logic
    const now = new Date();
    // 30 mins late threshold for "No Show" in completed list
    // Otherwise keep in Active list (maybe Delayed)
    const activeJobs = reservations.filter(r => {
        if (r.status === 'loading_completed') return false;
        if (r.status === 'pending') {
            const start = new Date(r.startTime);
            // If more than 30 mins late, move to completed as No Show
            if (now > new Date(start.getTime() + 30 * 60000)) return false;
        }
        return true;
    });

    // Sort active: Priority to Loading Started -> Checked In -> Pending
    activeJobs.sort((a, b) => {
        const order = { 'loading_started': 1, 'checked_in': 2, 'pending': 3 };
        return (order[a.status] || 99) - (order[b.status] || 99);
    });

    const completedJobs = reservations.filter(r => {
        if (r.status === 'loading_completed') return true;
        if (r.status === 'pending') {
            const start = new Date(r.startTime);
            if (now > new Date(start.getTime() + 30 * 60000)) return true;
        }
        return false;
    });

    if (!user?.tenantId) return <div className="p-xl text-center">Yetki yok.</div>;

    return (
        <div className="container pt-lg">
            <div className="flex justify-between items-center mb-lg">
                <div>
                    <h1>Depo / Rampa Operasyonu</h1>
                    <p className="text-secondary">Yükleme durumu ve rampa yönetimi.</p>
                </div>
                <Button variant="secondary" onClick={loadJobs} disabled={loading}>
                    <span className="material-icons">refresh</span>
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-xl">
                {/* ACTIVE JOBS */}
                <div>
                    <h3 className="mb-md flex items-center gap-sm">
                        <span className="badge badge-primary">{activeJobs.length}</span>
                        Bekleyen / Süren Yüklemeler
                    </h3>

                    <div className="flex flex-col gap-md">
                        {activeJobs.length === 0 && (
                            <div className="card p-lg text-center text-secondary">İşlem bekleyen araç yok.</div>
                        )}
                        {activeJobs.map(job => {
                            const action = getActionForStatus(job.status);
                            const isDelayed = new Date() > new Date(job.startTime);
                            const cardStyle = isDelayed
                                ? { borderLeft: '4px solid var(--status-delayed)', background: 'rgba(239, 68, 68, 0.05)' }
                                : { borderLeft: '4px solid var(--primary-color)' };

                            return (
                                <div key={job.id} className="card hover:bg-surface-subtle transition-colors" style={cardStyle}>
                                    <div className="flex justify-between mb-xs">
                                        <span className="font-bold text-lg">{job.dockName || 'Rampa Belirsiz'}</span>
                                        <div className="flex gap-2">
                                            {isDelayed && <span className="badge badge-delayed">GECİKTİ</span>}
                                            <StatusBadge status={job.status} />
                                        </div>
                                    </div>
                                    <div className="text-2xl font-mono font-bold mb-md">{job.plateNumber}</div>
                                    <div className="text-sm text-secondary mb-md">
                                        <div>{job.transporterName}</div>
                                        <div>Ürün: {job.productType || 'Genel Yük'}</div>
                                    </div>

                                    {job.note && (
                                        <div className="mb-md p-sm bg-amber-500/10 rounded-xl border border-amber-500/20">
                                            <div className="text-[10px] text-amber-500 font-bold uppercase mb-1 flex items-center gap-1">
                                                <span className="material-icons text-[12px]">sticky_note_2</span> Not
                                            </div>
                                            <div className="text-xs text-amber-100/90 italic whitespace-pre-wrap">{job.note}</div>
                                        </div>
                                    )}

                                    {action && (
                                        <Button
                                            variant={action.variant}
                                            className="w-full py-md text-lg"
                                            onClick={() => handleActionClick(job, action.type)}
                                        >
                                            {action.label}
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* COMPLETED JOBS */}
                <div>
                    <h3 className="mb-md flex items-center gap-sm">
                        <span className="badge badge-success">{completedJobs.length}</span>
                        Tamamlananlar / Gelmeyenler
                    </h3>
                    <div className="flex flex-col gap-sm">
                        {completedJobs.map(job => {
                            const isNoShow = job.status === 'pending'; // In completed list, pending means No Show
                            return (
                                <div
                                    key={job.id}
                                    className="card p-sm flex justify-between items-center"
                                    style={{
                                        background: isNoShow ? 'rgba(255, 255, 255, 0.02)' : 'rgba(16, 185, 129, 0.1)',
                                        border: isNoShow ? '1px dashed var(--text-secondary)' : '1px solid var(--status-completed)',
                                        opacity: isNoShow ? 0.6 : 1
                                    }}
                                >
                                    <div>
                                        <div className="font-mono font-bold" style={{ textDecoration: isNoShow ? 'line-through' : 'none' }}>
                                            {job.plateNumber}
                                        </div>
                                        <div className="text-xs text-secondary">{job.dockName}</div>
                                    </div>
                                    <div className="text-right">
                                        {isNoShow ? (
                                            <span className="badge badge-neutral" style={{ background: '#333', color: '#ccc' }}>GELMEDİ</span>
                                        ) : (
                                            <>
                                                <div className="text-xs text-secondary">Bitiş</div>
                                                <div className="font-mono">{job.loadingEndTime ? new Date(job.loadingEndTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {completedJobs.length === 0 && (
                            <div className="text-secondary text-sm">Henüz tamamlanan yükleme yok.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Action Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={actionType === 'START' ? 'Yüklemeyi Başlat' : 'Yüklemeyi Tamamla'}
            >
                {selectedJob && (
                    <div className="text-center">
                        <div className="text-4xl font-mono mb-lg">
                            {selectedJob.plateNumber}
                        </div>
                        <p className="text-secondary mb-xl">
                            {selectedJob.dockName} rampasında işlem
                            {actionType === 'START' ? ' başlatılacak.' : ' sonlandırılacak.'}
                        </p>

                        <div className="flex gap-md justify-end">
                            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>İptal</Button>
                            <Button
                                variant={actionType === 'START' ? 'primary' : 'success'}
                                onClick={performAction}
                            >
                                Onayla
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default LoadingList;
